import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR, formatNum, formatDate, formatDateShort, monthStr, presetRange, todayStr } from "@/lib/format";
import { SectionTitle, CHART_COLORS, CHART_GRID, CHART_AXIS, CHART_TOOLTIP } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { BarChart3, Download, ArrowUp, ArrowDown, Minus } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell, FunnelChart, Funnel, LabelList,
} from "recharts";

const PRESETS = [
  { id: "today", label: "Today" }, { id: "yesterday", label: "Yesterday" },
  { id: "this_week", label: "This week" }, { id: "last_7", label: "Last 7 days" },
  { id: "this_month", label: "This month" }, { id: "last_month", label: "Last month" },
];

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => r[c]).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Analytics() {
  const { activeEmployees } = useApp();
  const [range, setRange] = useState(presetRange("this_month"));
  const [emp, setEmp] = useState("all");
  const [gran, setGran] = useState("daily");
  const [data, setData] = useState(null);
  const [comp, setComp] = useState(null);

  useEffect(() => {
    api.analytics({ start: range.start, end: range.end, employee_id: emp, granularity: gran }).then(setData);
    api.monthlyComparison(monthStr(range.end), emp).then(setComp);
  }, [range, emp, gran]);

  const splits = data?.splits || {};
  const visitSplit = [{ name: "New", value: splits.new_visits || 0 }, { name: "Old", value: splits.old_visits || 0 }];
  const revSplit = [{ name: "New Revenue", value: splits.new_revenue || 0 }, { name: "Old Revenue", value: splits.old_revenue || 0 }];

  const perDayRows = (data?.per_day || []).map((d) => ({
    date: formatDate(d.date), dialed: d.dialed, connected: d.connected, leads: d.leads,
    visits: d.total_visits, new: d.new_visits, old: d.old_visits, treated: d.total_treated,
    revenue: d.revenue, connect_rate: d.connect_rate, treat_conv: d.treat_conv,
  }));

  return (
    <div className="animate-fade-up">
      <SectionTitle icon={BarChart3} title="Analytics & Charts" desc="Deep dive into funnel, trends and comparisons" />

      <div className="surface p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => setRange(presetRange(p.id))}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 text-slate-300 hover:bg-[#C9A24B]/15 hover:text-[#C9A24B] transition-colors">{p.label}</button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div><label className="text-[11px] text-slate-500 font-semibold">From</label><Input type="date" value={range.start} max={todayStr()} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="h-9 rounded-lg w-36" data-testid="analytics-start" /></div>
          <div><label className="text-[11px] text-slate-500 font-semibold">To</label><Input type="date" value={range.end} max={todayStr()} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="h-9 rounded-lg w-36" data-testid="analytics-end" /></div>
        </div>
        <Select value={emp} onValueChange={setEmp}>
          <SelectTrigger className="h-9 rounded-lg w-44" data-testid="analytics-employee"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Employees</SelectItem>{activeEmployees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={gran} onValueChange={setGran}>
          <SelectTrigger className="h-9 rounded-lg w-32" data-testid="analytics-granularity"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <ChartCard title="Revenue & Call Trend">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data?.series || []} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
              <XAxis dataKey="period" tickFormatter={(v) => (v && v.length > 7 ? formatDateShort(v) : v)} tick={{ fontSize: 11, fill: CHART_AXIS }} />
              <YAxis yAxisId="l" tick={{ fontSize: 11, fill: CHART_AXIS }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: CHART_AXIS }} />
              <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => (n === "Revenue" ? formatINR(v) : formatNum(v))} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" type="monotone" dataKey="revenue" stroke="#C9A24B" strokeWidth={2.5} dot={false} name="Revenue" />
              <Line yAxisId="r" type="monotone" dataKey="dialed" stroke="#38BDF8" strokeWidth={2} dot={false} name="Calls" />
              <Line yAxisId="r" type="monotone" dataKey="total_visits" stroke="#818CF8" strokeWidth={2} dot={false} name="Visits" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Conversion Funnel">
          <ResponsiveContainer width="100%" height={260}>
            <FunnelChart>
              <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => formatNum(v)} />
              <Funnel dataKey="value" data={data?.funnel || []} isAnimationActive>
                <LabelList position="right" fill="#cbd5e1" stroke="none" dataKey="stage" fontSize={12} />
                <LabelList position="center" fill="#0b1120" stroke="none" dataKey="value" fontSize={12} fontWeight={700} />
                {(data?.funnel || []).map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="New vs Old (Visits & Revenue)">
          <div className="grid grid-cols-2 gap-2">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={visitSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label><Cell fill="#C9A24B" /><Cell fill="#818CF8" /></Pie>
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => formatNum(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={revSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label><Cell fill="#38BDF8" /><Cell fill="#34D399" /></Pie>
                <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => formatINR(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Reasons for No Treatment">
          {(data?.reasons || []).length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-slate-500 text-sm">No no-treatment data in range</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.reasons} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {data.reasons.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={CHART_TOOLTIP} /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="surface p-5 mb-6">
        <div className="font-heading text-lg font-light text-white mb-4">Monthly Comparison — This Month vs Last Month{comp?.has_last_year ? " (& Last Year)" : ""}</div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="monthly-comparison-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10">
                <th className="py-2 pr-4">KPI</th><th className="py-2 px-4 text-right">This Month</th><th className="py-2 px-4 text-right">Last Month</th>
                {comp?.has_last_year && <th className="py-2 px-4 text-right">Last Year</th>}<th className="py-2 pl-4 text-right">Change</th>
              </tr>
            </thead>
            <tbody>
              {(comp?.rows || []).map((r) => {
                const isRev = r.kpi.includes("₹");
                const fmt = (v) => (isRev ? formatINR(v) : (r.kpi.includes("%") ? `${v}%` : formatNum(v)));
                const up = r.change > 0, down = r.change < 0;
                return (
                  <tr key={r.kpi} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2.5 pr-4 font-semibold text-slate-200">{r.kpi}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-white">{fmt(r.this_month)}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-slate-400">{fmt(r.last_month)}</td>
                    {comp?.has_last_year && <td className="py-2.5 px-4 text-right font-mono text-slate-500">{fmt(r.last_year)}</td>}
                    <td className="py-2.5 pl-4 text-right">
                      <span className={`inline-flex items-center gap-1 font-semibold ${up ? "text-emerald-400" : down ? "text-rose-400" : "text-slate-500"}`}>
                        {up ? <ArrowUp className="h-3.5 w-3.5" /> : down ? <ArrowDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}{Math.abs(r.change)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="surface p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg font-light text-white">Per-Day Log</div>
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="export-perday-btn" onClick={() => exportCSV(perDayRows, `per-day-${range.start}-to-${range.end}.csv`)}>
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm whitespace-nowrap" data-testid="per-day-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10">
                <th className="py-2 pr-4">Date</th><th className="py-2 px-3 text-right">Dialed</th><th className="py-2 px-3 text-right">Conn.</th>
                <th className="py-2 px-3 text-right">Leads</th><th className="py-2 px-3 text-right">Visits</th><th className="py-2 px-3 text-right">New</th>
                <th className="py-2 px-3 text-right">Old</th><th className="py-2 px-3 text-right">Treated</th><th className="py-2 px-3 text-right">Revenue</th>
                <th className="py-2 px-3 text-right">Conn%</th><th className="py-2 pl-3 text-right">Conv%</th>
              </tr>
            </thead>
            <tbody>
              {perDayRows.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-slate-500">No entries in range</td></tr>}
              {perDayRows.map((r, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 pr-4 font-semibold text-slate-200">{r.date}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.dialed)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.connected)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.leads)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.visits)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.new)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.old)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.treated)}</td>
                  <td className="py-2 px-3 text-right font-mono text-[#C9A24B]">{formatINR(r.revenue)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{r.connect_rate}%</td>
                  <td className="py-2 pl-3 text-right font-mono text-slate-300">{r.treat_conv}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="surface p-5">
      <div className="font-heading font-light text-white mb-3">{title}</div>
      {children}
    </div>
  );
}
